/**
 * PlatformAdmin — panel de administración de la propia CMDB (fuera del IdP).
 *
 * Solo para el superadmin global. Primera pestaña: qué entradas del menú
 * lateral oculta cada empresa a todos sus usuarios. Segunda: con qué aspecto
 * (estilo + color) arrancan esos usuarios. Las demás pestañas (SMTP y
 * diagnóstico del servidor) están previstas y se implementarán después.
 *
 * Importante: la política de menú es el techo — un usuario puede ocultar más,
 * pero nunca mostrar lo que aquí se oculte. El aspecto es lo contrario: es solo
 * el punto de partida, y cada usuario puede cambiarlo para sí mismo.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  BuildingOffice2Icon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import MenuVisibilityChecklist from './MenuVisibilityChecklist';
import LanguagesPanel from './LanguagesPanel';
import { MENU_CUSTOMIZER_GROUPS, PLATFORM_ADMIN_ITEM } from '../menuConfig';
import { SKINS, DEFAULT_SKIN } from '../theme/skins';
import { PALETTES } from '../theme/palettes';
import './PlatformAdmin.css';

const TABS = [
  { id: 'menu', label: 'Menú por empresa' },
  { id: 'appearance', label: 'Aspecto por empresa' },
  { id: 'languages', label: 'Idiomas' },
  { id: 'smtp', label: 'SMTP' },
  { id: 'diagnostics', label: 'Diagnóstico del servidor' },
];

function PlatformAdmin({ userInfo }) {
  const isSuperadmin = Boolean(userInfo?.is_superadmin);

  const [activeTab, setActiveTab] = useState('menu');
  const [companies, setCompanies] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [policy, setPolicy] = useState(() => new Set());
  const [policyCompanyId, setPolicyCompanyId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  // Borrador del aspecto por empresa, indexado por id: al cambiar de empresa se
  // ve el valor guardado y no se pisa lo que el superadmin esté tocando.
  const [themeDrafts, setThemeDrafts] = useState({});
  const [themeSaving, setThemeSaving] = useState(false);
  const [themeError, setThemeError] = useState('');
  const [themeSavedId, setThemeSavedId] = useState(null);
  const companyThemeDraft = (company) =>
    themeDrafts[company?.id] || {
      skin: company?.theme?.skin || DEFAULT_SKIN,
      palette: company?.theme?.palette || '',
    };

  useEffect(() => {
    if (!isSuperadmin) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([api.getPlatformSummary(), api.getPlatformCompaniesMenu()])
      .then(([summaryData, companyList]) => {
        if (cancelled) return;
        setSummary(summaryData);
        setCompanies(companyList);
        setLoadError('');
        setSelectedId((prev) => prev ?? companyList[0]?.id ?? null);
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error.message || 'No se pudo cargar el panel');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isSuperadmin]);

  const selected = useMemo(
    () => companies.find((company) => company.id === selectedId) || null,
    [companies, selectedId]
  );

  // Al cambiar de empresa, partir de la política guardada (una sola vez por
  // selección: si no, guardar reiniciaría los avisos de la pantalla).
  useEffect(() => {
    if (selectedId === policyCompanyId) return;
    const company = companies.find((item) => item.id === selectedId);
    setPolicy(new Set(company?.hidden_keys || []));
    setPolicyCompanyId(selectedId);
    setSaveError('');
    setSavedAt(null);
  }, [selectedId, policyCompanyId, companies]);

  const filteredCompanies = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return companies;
    return companies.filter(
      (company) =>
        company.name.toLowerCase().includes(query) ||
        String(company.id).includes(query) ||
        company.slug.toLowerCase().includes(query)
    );
  }, [companies, search]);

  // El superadmin ve el panel: la entrada de plataforma entra en el grupo de admin.
  const groups = useMemo(
    () =>
      MENU_CUSTOMIZER_GROUPS.map((group) =>
        group.key === 'admin'
          ? { ...group, items: [...group.items, PLATFORM_ADMIN_ITEM] }
          : group
      ),
    []
  );

  const allItems = useMemo(
    () => groups.reduce((acc, group) => acc.concat(group.items), []),
    [groups]
  );
  const visibleCount = allItems.filter((item) => !policy.has(item.key)).length;

  const selectedTheme = companyThemeDraft(selected);
  const hasFactoryTheme =
    selectedTheme.skin === DEFAULT_SKIN && !selectedTheme.palette;

  const updateThemeDraft = (patch) => {
    if (!selected) return;
    setThemeSavedId(null);
    setThemeError('');
    setThemeDrafts((prev) => ({
      ...prev,
      [selected.id]: { ...companyThemeDraft(selected), ...patch },
    }));
  };

  const toggleItem = (key) => {
    setSavedAt(null);
    setPolicy((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleGroup = (_group, showKeys) => {
    setSavedAt(null);
    setPolicy((prev) => {
      const next = new Set(prev);
      for (const key of showKeys) next.delete(key);
      return next;
    });
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    setSaveError('');
    try {
      const data = await api.savePlatformCompanyMenu(selected.id, [...policy]);
      const hiddenKeys = data.hidden_keys || [];
      setCompanies((prev) =>
        prev.map((company) =>
          company.id === selected.id
            ? { ...company, hidden_keys: hiddenKeys, has_policy: hiddenKeys.length > 0 }
            : company
        )
      );
      setPolicy(new Set(hiddenKeys));
      setSavedAt(new Date());
    } catch (error) {
      setSaveError(error.message || 'No se pudo guardar la política');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTheme = async (themeOverride = null) => {
    if (!selected) return;
    const draft = themeOverride || selectedTheme;
    setThemeSaving(true);
    setThemeError('');
    try {
      const theme = await api.savePlatformCompanyTheme(selected.id, {
        skin: draft.skin,
        palette: draft.palette || null,
      });
      setCompanies((prev) =>
        prev.map((company) =>
          company.id === selected.id
            ? {
                ...company,
                theme: {
                  skin: theme.skin,
                  palette: theme.palette,
                  has_theme: Boolean(theme.has_theme),
                },
              }
            : company
        )
      );
      setThemeDrafts((prev) => ({
        ...prev,
        [selected.id]: { skin: theme.skin, palette: theme.palette || '' },
      }));
      setThemeSavedId(selected.id);
    } catch (error) {
      setThemeError(error.message || 'No se pudo guardar el aspecto');
    } finally {
      setThemeSaving(false);
    }
  };

  /** Devuelve la empresa al aspecto de fábrica (Classic, color del usuario). */
  const handleResetTheme = () => {
    const factory = { skin: DEFAULT_SKIN, palette: '' };
    if (selected) {
      setThemeDrafts((prev) => ({ ...prev, [selected.id]: factory }));
    }
    handleSaveTheme(factory);
  };

  if (!isSuperadmin) {
    return (
      <div className="platform-admin">
        <div className="pa-denied">
          <ShieldCheckIcon />
          <h3>Solo para el superadmin de CMDB</h3>
          <p>
            Este panel configura la plataforma (menú de cada empresa, SMTP y diagnóstico),
            así que no está disponible para los administradores de una empresa.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="platform-admin">
      <header className="pa-header">
        <div>
          <h1 className="pa-title">Plataforma CMDB</h1>
          <p className="pa-subtitle">
            Administración de la propia CMDB, fuera del IdP: qué ve cada empresa y la
            configuración global del servidor.
          </p>
        </div>
        {summary && (
          <div className="pa-summary">
            <div className="pa-summary-item">
              <span className="pa-summary-value">{summary.active_companies}</span>
              <span className="pa-summary-label">empresas activas</span>
            </div>
            <div className="pa-summary-item">
              <span className="pa-summary-value">{summary.companies_with_menu_policy}</span>
              <span className="pa-summary-label">con menú limitado</span>
            </div>
            <div className="pa-summary-item">
              <span className="pa-summary-value">{summary.saved_menu_preferences}</span>
              <span className="pa-summary-label">preferencias guardadas</span>
            </div>
            <div className="pa-summary-item">
              <span className="pa-summary-value">{summary.companies_with_theme ?? 0}</span>
              <span className="pa-summary-label">con aspecto propio</span>
            </div>
          </div>
        )}
      </header>

      <nav className="pa-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`pa-tab ${activeTab === tab.id ? 'pa-tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeTab === 'languages' && <LanguagesPanel />}

      {activeTab !== 'menu' && activeTab !== 'appearance' && activeTab !== 'languages' && (
        <div className="pa-pending">
          <h3>{TABS.find((tab) => tab.id === activeTab)?.label}</h3>
          <p>
            Pendiente de implementar en la siguiente entrega. La pestaña queda reservada
            para no tener que reorganizar el panel cuando llegue.
          </p>
        </div>
      )}

      {(activeTab === 'menu' || activeTab === 'appearance') && (
        <>
          {loadError && <div className="alert alert-danger py-2">{loadError}</div>}
          {loading ? (
            <div className="pa-loading">Cargando empresas…</div>
          ) : (
            <div className="pa-layout">
              <aside className="pa-companies">
                <div className="pa-search">
                  <MagnifyingGlassIcon />
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Buscar empresa…"
                  />
                </div>
                <ul className="pa-company-list">
                  {filteredCompanies.map((company) => (
                    <li key={company.id}>
                      <button
                        type="button"
                        className={`pa-company ${company.id === selectedId ? 'pa-company--active' : ''}`}
                        onClick={() => setSelectedId(company.id)}
                      >
                        <BuildingOffice2Icon className="pa-company-icon" />
                        <span className="pa-company-texts">
                          <span className="pa-company-name">{company.name}</span>
                          <span className="pa-company-slug">{company.slug}</span>
                        </span>
                        {activeTab === 'appearance' ? (
                          company.theme?.has_theme ? (
                            <span className="pa-badge pa-badge--theme">{company.theme.skin}</span>
                          ) : (
                            <span className="pa-badge pa-badge--all">fábrica</span>
                          )
                        ) : company.has_policy ? (
                          <span className="pa-badge pa-badge--limit">{company.hidden_keys.length}</span>
                        ) : (
                          <span className="pa-badge pa-badge--all">todo</span>
                        )}
                      </button>
                    </li>
                  ))}
                  {!filteredCompanies.length && (
                    <li className="pa-empty">Sin resultados</li>
                  )}
                </ul>
              </aside>

              <section className="pa-detail">
                {!selected ? (
                  <div className="pa-loading">Elige una empresa de la lista.</div>
                ) : (
                  <>
                    <header className="pa-detail-header">
                      <div>
                        <h2 className="pa-detail-title">{selected.name}</h2>
                        <p className="pa-detail-meta">
                          <code>{selected.slug}</code> · id {selected.id}
                          {!selected.is_active && ' · inactiva'}
                        </p>
                      </div>
                      {activeTab === 'menu' ? (
                        <span className="pa-detail-count">
                          {visibleCount} de {allItems.length} visibles
                        </span>
                      ) : (
                        <span className="pa-detail-count">
                          {hasFactoryTheme
                            ? 'aspecto de fábrica'
                            : `${selectedTheme.skin}${selectedTheme.palette ? ` · ${selectedTheme.palette}` : ''}`}
                        </span>
                      )}
                    </header>

                    {activeTab === 'appearance' ? (
                      <section className="pa-theme">
                        <p className="pa-detail-help">
                          Aspecto con el que verán la CMDB los usuarios de esta empresa.
                          Es solo el punto de partida: cada uno puede cambiarlo para sí
                          mismo desde Ajustes → Apariencia. <strong>Classic</strong> es el
                          aspecto de siempre.
                        </p>

                        <div className="pa-theme-grid">
                          <label className="pa-field">
                            <span className="pa-field__label">Estilo</span>
                            <select
                              className="form-select"
                              value={selectedTheme.skin}
                              onChange={(event) =>
                                updateThemeDraft({ skin: event.target.value })
                              }
                            >
                              {SKINS.map((skin) => (
                                <option key={skin.id} value={skin.id}>
                                  {skin.name} — {skin.tagline}
                                </option>
                              ))}
                            </select>
                          </label>

                          <label className="pa-field">
                            <span className="pa-field__label">Color</span>
                            <select
                              className="form-select"
                              value={selectedTheme.palette}
                              onChange={(event) =>
                                updateThemeDraft({ palette: event.target.value })
                              }
                            >
                              <option value="">El que elija cada usuario</option>
                              {PALETTES.map((palette) => (
                                <option key={palette.id} value={palette.id}>
                                  {palette.name}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>

                        <p className="pa-theme-hint">
                          {SKINS.find((skin) => skin.id === selectedTheme.skin)?.description}
                        </p>

                        {themeError && (
                          <div className="alert alert-danger py-2">{themeError}</div>
                        )}
                        {themeSavedId === selected.id && !themeError && (
                          <div className="alert alert-success py-2">
                            Aspecto de empresa guardado. Se aplica a sus usuarios en la
                            próxima carga (si no han elegido otro).
                          </div>
                        )}

                        <footer className="pa-detail-actions">
                          <button
                            type="button"
                            className="quick-feedback-secondary"
                            onClick={handleResetTheme}
                            disabled={themeSaving}
                          >
                            Volver al aspecto de fábrica
                          </button>
                          <button
                            type="button"
                            className="quick-feedback-primary"
                            onClick={() => handleSaveTheme()}
                            disabled={themeSaving}
                          >
                            {themeSaving ? 'Guardando…' : 'Guardar aspecto'}
                          </button>
                        </footer>
                      </section>
                    ) : (
                      <>
                        <p className="pa-detail-help">
                          Marca lo que verán los usuarios de esta empresa. Lo que desmarques aquí
                          no lo podrán activar desde su personalizador.
                        </p>

                        {selected.users_with_preferences > 0 && (
                          <div className="alert alert-warning py-2">
                            {selected.users_with_preferences} usuario(s) de esta empresa ya tienen
                            preferencias guardadas; el cambio se les aplica al instante y solo
                            podrán ocultar más.
                          </div>
                        )}

                        {saveError && <div className="alert alert-danger py-2">{saveError}</div>}
                        {savedAt && !saveError && (
                          <div className="alert alert-success py-2">
                            Política guardada ({savedAt.toLocaleTimeString()}).
                          </div>
                        )}

                        <MenuVisibilityChecklist
                          groups={groups}
                          hiddenKeys={policy}
                          onToggleItem={toggleItem}
                          onToggleGroup={toggleGroup}
                        />

                        <footer className="pa-detail-actions">
                          <button
                            type="button"
                            className="quick-feedback-secondary"
                            onClick={() => { setPolicy(new Set()); setSavedAt(null); }}
                            disabled={saving}
                          >
                            Mostrar todo
                          </button>
                          <button
                            type="button"
                            className="quick-feedback-primary"
                            onClick={handleSave}
                            disabled={saving}
                          >
                            {saving ? 'Guardando…' : 'Guardar política'}
                          </button>
                        </footer>
                      </>
                    )}
                  </>
                )}
              </section>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default PlatformAdmin;
